import { z } from "zod";
import type { RuleSetMeta } from "../types.js";

/** AU-06 — HECS/HELP compulsory repayment tracker (FY26 marginal system). */

/**
 * Repayment income for study/training loan purposes (ATO definition). This is
 * NOT the same as taxable income: it adds back several amounts. We let the
 * caller either pass the components and have us sum them, or pass a single
 * pre-computed `repaymentIncome`. The breakdown is the surface the AU-06 UI
 * collects, so it's the primary path.
 *
 * Sum = taxable income
 *     + total net investment loss (financial + rental)
 *     + reportable fringe benefits amount
 *     + reportable (concessional) super contributions
 *     + exempt foreign employment income (s23AF / s23AG).
 *
 * Source: ATO "Study and training support loans rates and repayment
 * thresholds" / "Income tests".
 */
export const RepaymentIncomeComponentsSchema = z.object({
  /** Assessable income minus deductions (your taxable income). */
  taxableIncome: z.number().min(0),
  /** Total net investment loss (net financial + net rental property loss). */
  netInvestmentLoss: z.number().min(0).default(0),
  /** Reportable fringe benefits amount from your income statement. */
  reportableFringeBenefits: z.number().min(0).default(0),
  /** Reportable (salary-sacrificed / personal deductible) super contributions. */
  reportableSuperContributions: z.number().min(0).default(0),
  /** Exempt foreign employment income (s23AF / s23AG), net of expenses. */
  exemptForeignEmploymentIncome: z.number().min(0).default(0),
});

export type RepaymentIncomeComponents = z.infer<
  typeof RepaymentIncomeComponentsSchema
>;

export const Au06InputSchema = z
  .object({
    /**
     * Repayment income, either pre-computed or as components. Exactly one of
     * `repaymentIncome` / `income` must be supplied.
     */
    repaymentIncome: z.number().min(0).optional(),
    /** Component breakdown; summed into repayment income when present. */
    income: RepaymentIncomeComponentsSchema.optional(),
    /**
     * Current HELP/HECS balance, optional. When present we can express the
     * repayment as a fraction of the balance; we never invent a payoff date
     * the user didn't ask for. Indexation is NOT modelled here.
     */
    loanBalance: z.number().min(0).optional(),
    /**
     * The financial year being assessed, e.g. "2025-26". Passed in (never
     * derived from the clock) so historical re-runs resolve against the
     * ruleset in force that year.
     */
    assessmentYear: z.string(),
  })
  .refine(
    (v) => v.repaymentIncome !== undefined || v.income !== undefined,
    "supply either repaymentIncome or income components",
  );

export type Au06Input = z.infer<typeof Au06InputSchema>;

/**
 * A compulsory-repayment band in the marginal system. Repayment within a band
 * is `baseAmount` (the accumulated repayment at `from`) plus `rate` applied to
 * income above `from` — UNLESS `wholeIncome` is true, in which case `rate` is
 * applied to the WHOLE repayment income (the top band's "10% of total income"
 * rule, which is not marginal).
 */
export interface RepaymentBand {
  /** Lower bound of repayment income for this band, inclusive. */
  from: number;
  /** Upper bound, exclusive. `null` = unbounded (top band). */
  to: number | null;
  /** Accumulated compulsory repayment at exactly `from` (whole dollars). */
  baseAmount: number;
  /** Marginal rate applied within the band (fraction, e.g. 0.15 = 15c/$1). */
  rate: number;
  /**
   * When true the band charges `rate` × whole repayment income rather than
   * `baseAmount + rate × (income − from)`. Used for the top band only.
   */
  wholeIncome?: boolean;
}

export interface Au06RuleSet extends RuleSetMeta {
  /** Minimum repayment income below which no compulsory repayment is due. */
  minRepaymentThreshold: number;
  /** Marginal repayment bands, authored to cover [0, ∞). */
  bands: readonly RepaymentBand[];
}

export interface Au06Output {
  /** Repayment income the calculation ran against (summed or pre-computed). */
  repaymentIncome: number;
  /** Compulsory repayment for the FY, whole dollars (rounded). */
  compulsoryRepayment: number;
  /**
   * The marginal rate of the band the income falls in (the rate that applies
   * to the next dollar earned). For the top band this is the whole-income
   * rate. 0 below the minimum threshold.
   */
  marginalRate: number;
  /** Human-stable label for the band, e.g. "$67,001–$125,000". */
  thresholdBand: string;
  /** Whether any compulsory repayment is due at all. */
  repaymentDue: boolean;
  /**
   * Repayment income needed to reach the next band's floor, and the gap from
   * the current income. `null` when already in the top band.
   */
  nextBand: {
    /** Repayment income at which the next band starts. */
    at: number;
    /** Dollars of repayment income away from that floor. */
    away: number;
  } | null;
  /** Repayment as a fraction of the loan balance, when a balance was given. */
  fractionOfBalance: number | null;
}
